package fr.frux.core.designsystem.components

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.selection.selectable
import androidx.compose.foundation.selection.selectableGroup
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.outlined.Flag
import androidx.compose.material.icons.outlined.Visibility
import androidx.compose.material.icons.outlined.VisibilityOff
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.DropdownMenuItem
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.RadioButton
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.saveable.rememberSaveable
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.semantics.Role
import androidx.compose.ui.unit.dp
import fr.frux.core.common.error.AppError
import fr.frux.core.designsystem.error.userMessage
import fr.frux.core.designsystem.resources.Res
import fr.frux.core.designsystem.resources.ds_moderation_block
import fr.frux.core.designsystem.resources.ds_moderation_block_dialog_confirm
import fr.frux.core.designsystem.resources.ds_moderation_block_dialog_text
import fr.frux.core.designsystem.resources.ds_moderation_block_dialog_title
import fr.frux.core.designsystem.resources.ds_moderation_blocked
import fr.frux.core.designsystem.resources.ds_moderation_cancel
import fr.frux.core.designsystem.resources.ds_moderation_content_reported
import fr.frux.core.designsystem.resources.ds_moderation_details_label
import fr.frux.core.designsystem.resources.ds_moderation_reason_harassment
import fr.frux.core.designsystem.resources.ds_moderation_reason_other
import fr.frux.core.designsystem.resources.ds_moderation_reason_spam
import fr.frux.core.designsystem.resources.ds_moderation_reason_username
import fr.frux.core.designsystem.resources.ds_moderation_report_description
import fr.frux.core.designsystem.resources.ds_moderation_report_send
import fr.frux.core.designsystem.resources.ds_moderation_report_sheet_text
import fr.frux.core.designsystem.resources.ds_moderation_report_sheet_title
import fr.frux.core.designsystem.resources.ds_moderation_report_user
import fr.frux.core.designsystem.resources.ds_moderation_unblock
import fr.frux.core.designsystem.resources.ds_moderation_unblocked
import fr.frux.core.designsystem.resources.ds_moderation_user_reported
import fr.frux.core.designsystem.theme.FruxSpacing
import org.jetbrains.compose.resources.stringResource

/** Motifs d'un signalement de compte (ADR 0029). Le `name` est le code envoyé au serveur. */
enum class UserReportReason { INAPPROPRIATE_USERNAME, SPAM, HARASSMENT, OTHER }

/**
 * Borne du champ « Précisions », miroir de `UserReportLimits.DETAILS_MAX` (backend-shared, que le
 * design system ne voit pas). Le serveur reste la borne finale (400 `DETAILS_TOO_LONG`).
 */
private const val REPORT_DETAILS_MAX_LENGTH = 1000

/** Retour d'un geste de modération, à afficher en snackbar puis consommer par le ViewModel. */
sealed interface UserModerationMessage {
    data class Blocked(
        val username: String,
    ) : UserModerationMessage

    data class Unblocked(
        val username: String,
    ) : UserModerationMessage

    data object UserReported : UserModerationMessage

    data object ContentReported : UserModerationMessage

    data class Failed(
        val error: AppError,
    ) : UserModerationMessage
}

@Composable
fun UserModerationMessage.message(): String =
    when (this) {
        is UserModerationMessage.Blocked -> stringResource(Res.string.ds_moderation_blocked, username)
        is UserModerationMessage.Unblocked -> stringResource(Res.string.ds_moderation_unblocked, username)
        UserModerationMessage.UserReported -> stringResource(Res.string.ds_moderation_user_reported)
        UserModerationMessage.ContentReported -> stringResource(Res.string.ds_moderation_content_reported)
        is UserModerationMessage.Failed -> error.userMessage()
    }

/**
 * Entrées « Signaler ce compte » et « Masquer » ou « Réafficher » d'un menu de fiche. À placer
 * dans un `DropdownMenu` : chaque entrée ferme le menu avant d'agir.
 */
@Composable
fun UserModerationMenuItems(
    username: String,
    isBlocked: Boolean,
    onReportUser: () -> Unit,
    onBlock: () -> Unit,
    onUnblock: () -> Unit,
    onDismissMenu: () -> Unit,
) {
    DropdownMenuItem(
        text = { Text(stringResource(Res.string.ds_moderation_report_user)) },
        leadingIcon = { Icon(Icons.Outlined.Flag, contentDescription = null) },
        onClick = {
            onDismissMenu()
            onReportUser()
        },
    )
    if (isBlocked) {
        DropdownMenuItem(
            text = { Text(stringResource(Res.string.ds_moderation_unblock, username)) },
            leadingIcon = { Icon(Icons.Outlined.Visibility, contentDescription = null) },
            onClick = {
                onDismissMenu()
                onUnblock()
            },
        )
    } else {
        DropdownMenuItem(
            text = { Text(stringResource(Res.string.ds_moderation_block, username)) },
            leadingIcon = { Icon(Icons.Outlined.VisibilityOff, contentDescription = null) },
            onClick = {
                onDismissMenu()
                onBlock()
            },
        )
    }
}

/** Entrée « Signaler la description », proposée seulement quand une description existe. */
@Composable
fun ReportContentMenuItem(
    onClick: () -> Unit,
    onDismissMenu: () -> Unit,
) {
    DropdownMenuItem(
        text = { Text(stringResource(Res.string.ds_moderation_report_description)) },
        leadingIcon = { Icon(Icons.Outlined.Flag, contentDescription = null) },
        onClick = {
            onDismissMenu()
            onClick()
        },
    )
}

@Composable
private fun UserReportReason.label(): String =
    when (this) {
        UserReportReason.INAPPROPRIATE_USERNAME -> stringResource(Res.string.ds_moderation_reason_username)
        UserReportReason.SPAM -> stringResource(Res.string.ds_moderation_reason_spam)
        UserReportReason.HARASSMENT -> stringResource(Res.string.ds_moderation_reason_harassment)
        UserReportReason.OTHER -> stringResource(Res.string.ds_moderation_reason_other)
    }

/** Formulaire de signalement d'un compte : un motif obligatoire, des précisions facultatives. */
@Composable
fun ReportUserForm(
    username: String,
    onSubmit: (UserReportReason, String?) -> Unit,
    modifier: Modifier = Modifier,
) {
    var reason by rememberSaveable { mutableStateOf<UserReportReason?>(null) }
    var details by rememberSaveable { mutableStateOf("") }
    Column(modifier = modifier, verticalArrangement = Arrangement.spacedBy(FruxSpacing.sm)) {
        Text(
            text = stringResource(Res.string.ds_moderation_report_sheet_title, username),
            style = MaterialTheme.typography.titleLarge,
        )
        Text(
            text = stringResource(Res.string.ds_moderation_report_sheet_text),
            style = MaterialTheme.typography.bodyMedium,
            color = MaterialTheme.colorScheme.onSurfaceVariant,
        )
        Column(Modifier.selectableGroup()) {
            UserReportReason.entries.forEach { option ->
                Row(
                    modifier =
                        Modifier
                            .fillMaxWidth()
                            .heightIn(min = 48.dp)
                            .selectable(
                                selected = reason == option,
                                onClick = { reason = option },
                                role = Role.RadioButton,
                            ),
                    verticalAlignment = Alignment.CenterVertically,
                ) {
                    RadioButton(selected = reason == option, onClick = null)
                    Spacer(Modifier.width(FruxSpacing.sm))
                    Text(option.label(), style = MaterialTheme.typography.bodyLarge)
                }
            }
        }
        FruxTextField(
            value = details,
            onValueChange = { details = it },
            label = stringResource(Res.string.ds_moderation_details_label),
            singleLine = false,
            maxLength = REPORT_DETAILS_MAX_LENGTH,
            modifier = Modifier.fillMaxWidth(),
        )
        Spacer(Modifier.height(FruxSpacing.xs))
        FruxButton(
            text = stringResource(Res.string.ds_moderation_report_send),
            onClick = { reason?.let { onSubmit(it, details.trim().ifEmpty { null }) } },
            enabled = reason != null,
            modifier = Modifier.fillMaxWidth(),
        )
    }
}

/** [ReportUserForm] en feuille modale. [onSubmit] ne ferme pas la feuille : l'appelant le fait. */
@Composable
fun ReportUserSheet(
    username: String,
    onSubmit: (UserReportReason, String?) -> Unit,
    onDismiss: () -> Unit,
) {
    FruxModalBottomSheet(onDismiss = onDismiss, skipPartiallyExpanded = true) {
        ReportUserForm(
            username = username,
            onSubmit = onSubmit,
            modifier = Modifier.fillMaxWidth().padding(horizontal = FruxSpacing.md).padding(bottom = FruxSpacing.lg),
        )
    }
}

/** Confirmation du masquage : dit ce que le geste fait, et qu'il ne prévient personne. */
@Composable
fun BlockUserDialog(
    username: String,
    onConfirm: () -> Unit,
    onDismiss: () -> Unit,
) {
    AlertDialog(
        onDismissRequest = onDismiss,
        title = { Text(stringResource(Res.string.ds_moderation_block_dialog_title, username)) },
        text = { Text(stringResource(Res.string.ds_moderation_block_dialog_text)) },
        confirmButton = {
            TextButton(onClick = onConfirm) { Text(stringResource(Res.string.ds_moderation_block_dialog_confirm)) }
        },
        dismissButton = {
            TextButton(onClick = onDismiss) { Text(stringResource(Res.string.ds_moderation_cancel)) }
        },
    )
}
